import { useEffect, useState } from "react";
import { ArrowDownIcon } from "@/assets/icons";
import { RolesEnum } from "@/helpers/constants";
import { useIsEnterpriseUser } from "@/helpers/hooks";
import { isAdmin, isOwner } from "@/services/auth";
import { Stack, Tooltip, Button } from "@altimateai/lego";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@altimateai/lego";
import { IconAlertTriangle } from "@tabler/icons-react";

// Role hierarchy: the current user may only assign roles within their own tier
// (their role is the max they can grant). Mirrors the backend ROLE_HIERARCHY —
// an Admin cannot promote anyone to Owner. Keep these in sync.
const assignableRoles = (): string[] => {
  if (isOwner()) return [RolesEnum.OWNER, RolesEnum.ADMIN, RolesEnum.USER];
  if (isAdmin()) return [RolesEnum.ADMIN, RolesEnum.USER];
  return [RolesEnum.USER];
};

const getStyledRolesList = (isEnterpriseUser: boolean) => {
  return isEnterpriseUser
    ? [
        { label: RolesEnum.OWNER, value: RolesEnum.OWNER },
        { label: RolesEnum.ADMIN, value: RolesEnum.ADMIN },
        { label: RolesEnum.USER, value: RolesEnum.USER },
      ]
    : [
        {
          label: RolesEnum.USER,
          value: RolesEnum.USER,
        },
        {
          label: RolesEnum.ADMIN,
          value: RolesEnum.ADMIN,
          disabled: true,
          enterpriseUserBadge: true,
        },
        {
          label: RolesEnum.OWNER,
          value: RolesEnum.OWNER,
        },
      ];
};

type RolesDropdownProps = {
  id: string;
  userRole: string;
  setUserRole: (role: string) => void;
  dispDropdown: boolean;
  roleSource?: string | null;
  hasRoleConflict?: boolean;
  ssoResolvedRoleTitle?: string | null;
  onResolveConflict?: () => void;
};

const RolesDropdown = ({
  id,
  userRole,
  setUserRole,
  dispDropdown,
  roleSource,
  hasRoleConflict,
  ssoResolvedRoleTitle,
  onResolveConflict,
}: RolesDropdownProps) => {
  const { isEnterpriseUser } = useIsEnterpriseUser();
  const [manuallyChanged, setManuallyChanged] = useState(false);
  const isSsoManaged = roleSource === "sso_group";
  const showSso = isSsoManaged && !manuallyChanged && !hasRoleConflict;

  // Reset when server data changes (roleSource prop updated after refetch)
  useEffect(() => {
    setManuallyChanged(false);
  }, [roleSource]);

  const displayRole = userRole ? `${userRole}${showSso ? " (SSO)" : ""}` : "Role";

  const conflictIndicator = hasRoleConflict ? (
    <Tooltip
      title={`SSO suggests "${ssoResolvedRoleTitle ?? "a different role"}" — click to resolve`}
    >
      <Button
        variant="ghost"
        size="sm"
        className="al-p-0"
        onClick={() => onResolveConflict?.()}
        aria-label="Resolve role conflict"
      >
        <IconAlertTriangle size={16} className="al-text-yellow-600" />
      </Button>
    </Tooltip>
  ) : null;

  return (
    <Stack direction="row" className="al-items-center al-gap-1">
      {dispDropdown ? (
        <DropdownMenu>
          <DropdownMenuTrigger id={id} asChild>
            <Stack
              direction="row"
              className="al-bg-[rgba(17,30,49,0.04)] al-rounded-[12px] al-p-[0.8em] al-flex-grow al-text-[#082247] al-text-[1em] al-cursor-pointer al-justify-between al-items-center"
              style={{ width: "100%" }}
            >
              <span>{displayRole}</span>
              <ArrowDownIcon className="al-w-4 al-h-4 al-text-[#8390a3]" />
            </Stack>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {getStyledRolesList(isEnterpriseUser)
              .filter(option => assignableRoles().includes(option.value))
              .map(option => (
                <DropdownMenuItem
                  key={option.value}
                  disabled={option.disabled}
                  onClick={() => {
                    setUserRole(option.value);
                    setManuallyChanged(true);
                  }}
                  className="al-cursor-pointer"
                >
                  <Stack direction="row" className="al-items-center al-justify-between al-w-full">
                    <span>{option.label}</span>
                    {option.enterpriseUserBadge && (
                      <span className="al-text-xs al-bg-gray-100 al-px-2 al-py-1 al-rounded">
                        Enterprise
                      </span>
                    )}
                  </Stack>
                </DropdownMenuItem>
              ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        displayRole
      )}
      {conflictIndicator}
    </Stack>
  );
};

export default RolesDropdown;
